import { useState, useEffect, useMemo } from 'react';
import { api } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, User, Mail, Phone, CreditCard, Edit, Eye, Search, 
  Users, ChevronLeft, ChevronRight, Camera, Loader2, CalendarPlus
} from 'lucide-react';
import { toast } from 'sonner';
import { useTabs, TAB_TYPES } from '@/context/TabsContext';

const ITEMS_PER_PAGE = 15;

export default function GuestManagement({ isTab, tabId }) {
  const { openTab } = useTabs();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [idPreviewOpen, setIdPreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const guestsRes = await api.get('/guests');
      setGuests(guestsRes.data);
    } catch (error) {
      console.error('Failed to load guest data:', error);
      toast.error('Failed to load data. Please refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  // Filter guests based on search query
  const filteredGuests = useMemo(() => {
    if (!searchQuery.trim()) return guests;
    
    const query = searchQuery.toLowerCase();
    return guests.filter(guest => 
      guest.name?.toLowerCase().includes(query) ||
      guest.email?.toLowerCase().includes(query) ||
      guest.phone?.includes(query) ||
      guest.id_number?.toLowerCase().includes(query) ||
      guest.id_type_name?.toLowerCase().includes(query)
    );
  }, [guests, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredGuests.length / ITEMS_PER_PAGE);
  const paginatedGuests = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredGuests.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredGuests, currentPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Open guest details in a tab
  const openGuestDetailsTab = (guest) => {
    openTab({
      type: TAB_TYPES.GUEST_DETAILS,
      id: guest.id,
      title: guest.name || `Guest #${guest.id.slice(-6)}`,
      data: { guestId: guest.id, guest, onRefresh: loadData }
    });
  };

  // Open guest edit in a tab
  const openGuestEditTab = (guest) => {
    openTab({
      type: TAB_TYPES.GUEST_EDIT,
      id: `edit-${guest.id}`,
      title: `Edit ${guest.name}`,
      data: { guestId: guest.id, guest, onRefresh: loadData }
    });
  };

  // Open new booking with pre-filled guest details
  const openNewBookingWithGuest = (guest) => {
    openTab({
      type: TAB_TYPES.NEW_BOOKING,
      id: `new-booking-guest-${guest.id}`,
      title: `New Booking - ${guest.name}`,
      data: { 
        prefillGuest: guest,
        onComplete: loadData 
      }
    });
  };

  // Open ID proof in popup
  const openIdPreview = (imageUrl) => {
    const fullUrl = imageUrl.startsWith('http') ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`;
    setPreviewImageUrl(fullUrl);
    setIdPreviewOpen(true);
  };

  // Open Add Guest in a new tab
  const openAddGuestTab = () => {
    openTab({
      type: TAB_TYPES.GUEST_ADD,
      id: `add-guest-${Date.now()}`,
      title: 'Add New Guest',
      data: { onRefresh: loadData }
    });
  };

  return (
    <div className="space-y-4" data-testid="guest-management">
      {/* Header with Search and Add Button */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800" style={{ fontFamily: 'Manrope' }}>Guest Management</h1>
          <p className="text-gray-500 text-sm mt-1">
            {filteredGuests.length} guest{filteredGuests.length !== 1 ? 's' : ''} 
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>
        
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, phone, email, ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 border-orange-200 focus:border-orange-400"
              data-testid="guest-search-input"
            />
          </div>
          
          {/* Add Guest Button - Opens in new tab */}
          <Button 
            onClick={openAddGuestTab} 
            className="bg-gradient-orange hover:opacity-90 text-white btn-glow whitespace-nowrap" 
            data-testid="add-guest-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Guest
          </Button>
        </div>
      </div>

      {/* Data Grid / Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-10 w-10 text-orange-500 animate-spin" />
        </div>
      ) : filteredGuests.length === 0 ? (
        <Card className="border-orange-100">
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 mx-auto text-orange-300 mb-4" />
            {searchQuery ? (
              <>
                <p className="text-gray-500">No guests found matching &quot;{searchQuery}&quot;</p>
                <p className="text-sm text-gray-400 mt-2">Try a different search term</p>
              </>
            ) : (
              <>
                <p className="text-gray-500">No guests registered yet</p>
                <p className="text-sm text-gray-400 mt-2">Click &quot;Add Guest&quot; to register a new guest</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-orange-100 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gradient-to-r from-orange-50 to-purple-50 hover:from-orange-50 hover:to-purple-50">
                  <TableHead className="font-semibold text-gray-700">Name</TableHead>
                  <TableHead className="font-semibold text-gray-700">Phone</TableHead>
                  <TableHead className="font-semibold text-gray-700">Email</TableHead>
                  <TableHead className="font-semibold text-gray-700">ID Type</TableHead>
                  <TableHead className="font-semibold text-gray-700">ID Number</TableHead>
                  <TableHead className="font-semibold text-gray-700">ID Proof</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedGuests.map((guest, index) => (
                  <TableRow 
                    key={guest.id} 
                    className={`hover:bg-orange-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
                    data-testid={`guest-row-${guest.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-100 to-purple-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{guest.name}</p>
                          <p className="text-xs text-gray-400">#{guest.id.slice(-8)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                        {guest.phone || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-gray-600">
                        {guest.email ? (
                          <>
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            <span className="truncate max-w-[180px]">{guest.email}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {guest.id_type_name ? (
                        <Badge variant="outline" className="border-orange-200 text-orange-700 bg-orange-50">
                          {guest.id_type_name}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <CreditCard className="h-3.5 w-3.5 text-gray-400" />
                        {guest.id_number || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {guest.id_proof_image ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openIdPreview(guest.id_proof_image)}
                          className="h-7 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                        >
                          <Camera className="h-3 w-3 mr-1" />
                          View
                        </Button>
                      ) : (
                        <span className="text-gray-400 text-xs">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openNewBookingWithGuest(guest)} 
                          className="h-8 w-8 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50"
                          title="New Booking"
                          data-testid={`new-booking-guest-${guest.id}`}
                        >
                          <CalendarPlus className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openGuestDetailsTab(guest)} 
                          className="h-8 w-8 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50"
                          title="View Details"
                          data-testid={`view-guest-${guest.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => openGuestEditTab(guest)} 
                          className="h-8 w-8 text-gray-500 hover:text-orange-600 hover:bg-orange-50"
                          title="Edit Guest"
                          data-testid={`edit-guest-${guest.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-orange-100 bg-gray-50/50">
              <p className="text-sm text-gray-500">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} - {Math.min(currentPage * ITEMS_PER_PAGE, filteredGuests.length)} of {filteredGuests.length} guests
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-8 border-orange-200"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className={`h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-500 hover:bg-orange-600' : 'border-orange-200'}`}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-8 border-orange-200"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* ID Proof Preview Dialog */}
      <Dialog open={idPreviewOpen} onOpenChange={setIdPreviewOpen}>
        <DialogContent className="max-w-2xl bg-white p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b">
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-orange-500" />
              ID Proof Document
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 flex items-center justify-center bg-gray-50 min-h-[300px]">
            {previewImageUrl && (
              <img 
                src={previewImageUrl} 
                alt="ID Proof" 
                className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
              />
            )}
          </div>
          <div className="p-3 border-t bg-gray-50 flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(previewImageUrl, '_blank')}
              className="border-orange-200"
            >
              <Eye className="h-4 w-4 mr-1" />
              Open Full Size
            </Button>
            <Button
              size="sm"
              onClick={() => setIdPreviewOpen(false)}
              className="bg-gradient-orange text-white"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
